import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Ticket, Plus, Trash2 } from 'lucide-react';
import { adminFetch } from './shared';

export const CouponsTab = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', type: 'percent', value: 10, min_order: 0, max_uses: 0, expires_at: '', active: true });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const res = await adminFetch('/api/admin/coupons');
      if (res.ok) setCoupons(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      const body = {
        ...form,
        code: form.code.trim().toUpperCase(),
        value: parseFloat(form.value),
        min_order: parseFloat(form.min_order) || 0,
        max_uses: parseInt(form.max_uses, 10) || 0,
        expires_at: form.expires_at || null,
      };
      const res = await adminFetch('/api/admin/coupons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        await fetchCoupons();
        setShowForm(false);
        setForm({ code: '', type: 'percent', value: 10, min_order: 0, max_uses: 0, expires_at: '', active: true });
      } else {
        const d = await res.json();
        setFormError(d.detail || 'Error al crear');
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id, current) => {
    await adminFetch(`/api/admin/coupons/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !current }),
    });
    fetchCoupons();
  };

  const deleteCoupon = async (id, code) => {
    if (!window.confirm(`¿Eliminar cupón ${code}? Esto no se puede deshacer.`)) return;
    await adminFetch(`/api/admin/coupons/${id}`, { method: 'DELETE' });
    fetchCoupons();
  };

  return (
    <div className="space-y-4" data-testid="coupons-tab">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          {coupons.length} cupón{coupons.length !== 1 ? 'es' : ''} · {coupons.filter(c => c.active).length} activo{coupons.filter(c => c.active).length !== 1 ? 's' : ''}
        </p>
        <Button onClick={() => setShowForm(!showForm)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="new-coupon-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo cupón
        </Button>
      </div>

      {showForm && (
        <Card className="bg-slate-50 border-cyan-300 border-2">
          <form onSubmit={handleCreate} className="p-4 space-y-3" data-testid="coupon-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Código</label>
                <input
                  type="text" required value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="MITZVA10"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  autoCapitalize="characters" data-testid="coupon-code"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg" data-testid="coupon-type">
                  <option value="percent">% Descuento</option>
                  <option value="fixed">Monto fijo MXN</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  {form.type === 'percent' ? 'Porcentaje (1-100)' : 'Monto MXN'}
                </label>
                <input
                  type="number" required min="1" step="0.01" value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg"
                  data-testid="coupon-value"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Pedido mínimo MXN</label>
                <input
                  type="number" min="0" step="0.01" value={form.min_order}
                  onChange={(e) => setForm({ ...form, min_order: e.target.value })}
                  placeholder="0 = sin mínimo"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg"
                  data-testid="coupon-min"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Usos máximos</label>
                <input
                  type="number" min="0" step="1" value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder="0 = ilimitado"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg"
                  data-testid="coupon-max"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Expira (opcional)</label>
                <input
                  type="date" value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg"
                  data-testid="coupon-expires"
                />
              </div>
            </div>
            {formError && <p className="text-rose-600 text-xs" data-testid="coupon-form-error">{formError}</p>}
            <div className="flex gap-2 justify-end pt-1">
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
                {saving ? 'Guardando...' : 'Crear cupón'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {loading && coupons.length === 0 ? (
        <div className="text-center py-10 text-slate-500 text-sm">Cargando cupones...</div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm">Aún no has creado cupones.</div>
      ) : (
        <div className="space-y-2">
          {coupons.map(c => {
            const expired = c.expires_at && new Date(c.expires_at) < new Date();
            const exhausted = c.max_uses > 0 && c.used_count >= c.max_uses;
            return (
              <Card key={c.id} className={`border ${c.active && !expired && !exhausted ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'}`}>
                <div className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${c.active && !expired && !exhausted ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      <Ticket className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-slate-800 tracking-wide">{c.code}</p>
                      <p className="text-xs text-slate-500">
                        {c.type === 'percent' ? `${c.value}% off` : `$${c.value} MXN off`}
                        {c.min_order > 0 && ` · mín. $${c.min_order}`}
                        {c.max_uses > 0 && ` · ${c.used_count}/${c.max_uses} usos`}
                        {c.max_uses === 0 && ` · ${c.used_count} usos`}
                        {c.expires_at && ` · expira ${c.expires_at.slice(0, 10)}`}
                      </p>
                      {(expired || exhausted) && <span className="text-[10px] text-rose-600 font-semibold">{expired ? 'EXPIRADO' : 'AGOTADO'}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => toggleActive(c.id, c.active)} disabled={expired || exhausted}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full ${c.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'} disabled:opacity-50`}
                      data-testid={`coupon-toggle-${c.code}`}>
                      {c.active ? 'Activo' : 'Inactivo'}
                    </button>
                    <button onClick={() => deleteCoupon(c.id, c.code)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded" data-testid={`coupon-delete-${c.code}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
